import { useEffect, useMemo, useState } from 'react';
import { ApiError, type BlogPostDetail, type BlogPostSummary } from '@chicare/shared';

import {
  useAdminPost,
  useAdminPosts,
  useDeletePost,
  useRestorePostRevision,
  useSavePost,
  useUploadMedia,
} from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MarkdownEditor } from '@/components/ui/MarkdownEditor';
import { countWords, useUnsavedGuard } from '@/lib/prose';
import { SearchField } from '@/components/ui/SearchField';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from './AdminError';

/**
 * The blog desk.
 *
 * Master–detail, like the pages screen beside it, because somebody editing
 * three drafts in a row should not lose their place in the list between saves.
 *
 * ── WHAT THIS SCREEN HAS TO GET RIGHT ───────────────────────────────────────
 *
 * 1. PUBLISHING IS A DIFFERENT PERMISSION. A content writer sees "Save draft"
 *    and no Publish button at all — not a Publish button that answers 403.
 *    `can_publish` comes from the server with the list, because the client must
 *    never be the thing deciding.
 *
 * 2. THE SHELF IS A DELIBERATE CHOICE. Human, animal health, or both. "Both"
 *    is correct for "how to spot a counterfeit pack" and wrong for anything
 *    about dosing, so all three are spelled out rather than one being the
 *    silent default of an empty field.
 *
 * 3. TWENTY MINUTES OF WRITING IS NOT DISPOSABLE. A dirty editor warns before
 *    the tab closes and before the selection changes.
 */
export function AdminBlogScreen() {
  const [filter, setFilter] = useState<{ status?: string; q?: string }>({});
  const { data, isLoading, isError, error, refetch } = useAdminPosts(filter);

  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [dirty, setDirty] = useState(false);

  const activeSlug = composing ? null : (selected ?? undefined);

  if (isError) {
    return (
      <AdminError title="We couldn't load the articles" error={error} onRetry={() => refetch()} />
    );
  }

  /** Changing what is open discards unsaved work, so it asks first. */
  const switchTo = (next: () => void) => {
    if (dirty && !window.confirm('You have unsaved changes. Leave them?')) return;

    setDirty(false);
    next();
  };

  const counts = {
    draft: data?.posts.filter((one) => one.status === 'draft').length ?? 0,
    published: data?.posts.filter((one) => one.status === 'published').length ?? 0,
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Blog</h1>
          <p className="mt-1 max-w-[72ch] text-[13px] text-muted">
            Articles for buyers. Every save keeps the version it replaced, and an article
            carries the byline of whoever wrote it rather than whoever saved last.
            {data && !data.can_publish ? (
              <>
                {' '}
                <b className="text-ink">
                  You can write and edit. Somebody with the publishing permission reviews and
                  publishes.
                </b>
              </>
            ) : null}
          </p>
        </div>

        <Button
          icon="plus"
          onClick={() => switchTo(() => {
            setComposing(true);
            setSelected(null);
          })}
          data-testid="new-article"
          className="w-auto"
        >
          New article
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr] lg:items-start">
        {/* ── The list ────────────────────────────────────────────────── */}
        <Card flush className="lg:sticky lg:top-4">
          <div className="border-b border-line-2 p-2.5">
            <SearchField
              value={filter.q ?? ''}
              onChange={(event) => setFilter({ ...filter, q: event.target.value })}
              onClear={() => setFilter({ ...filter, q: '' })}
              placeholder="Search by title"
              aria-label="Search articles"
            />

            <div className="mt-2 flex gap-1">
              {(
                [
                  [undefined, `All ${data ? data.posts.length : ''}`],
                  ['draft', `Drafts ${counts.draft || ''}`],
                  ['published', `Live ${counts.published || ''}`],
                ] as const
              ).map(([status, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setFilter({ ...filter, status })}
                  aria-pressed={filter.status === status}
                  className={cn(
                    'min-h-9 flex-1 rounded-btn border px-2 text-[11.5px] font-semibold',
                    filter.status === status
                      ? 'border-blue bg-blue-tint text-blue'
                      : 'border-line bg-white text-muted hover:text-ink',
                  )}
                >
                  {label.trim()}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-1.5 p-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" radius="md" />
              ))}
            </div>
          ) : data?.posts.length === 0 ? (
            <p className="m-0 p-6 text-center text-[12.5px] text-faint">
              {filter.q || filter.status
                ? 'Nothing matches that.'
                : 'No articles yet. Press “New article”.'}
            </p>
          ) : (
            <ul className="m-0 max-h-[70vh] list-none divide-y divide-line-2 overflow-y-auto p-0">
              {data?.posts.map((post) => (
                <li key={post.id}>
                  <button
                    type="button"
                    onClick={() => switchTo(() => {
                      setComposing(false);
                      setSelected(post.slug);
                    })}
                    className={cn(
                      'w-full px-3.5 py-3 text-left',
                      post.slug === activeSlug
                        ? 'bg-blue-tint-2 shadow-[inset_3px_0_0_var(--color-blue)]'
                        : 'hover:bg-paper',
                    )}
                  >
                    <b className="block truncate text-[13px]">{post.title}</b>

                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <Badge tone={post.status === 'published' ? 'verified' : 'caution'}>
                        {post.status}
                      </Badge>
                      {post.category ? (
                        <span className="text-[10.5px] text-muted">{post.category}</span>
                      ) : null}
                      <ShelfMark sector={post.sector} />
                    </div>

                    <span className="mt-1 block truncate text-[10.5px] text-faint">
                      {post.author}
                      {post.reading_minutes ? ` · ${post.reading_minutes} min` : ''}
                      {post.revision_count > 0 ? ` · ${post.revision_count} saved versions` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ── The editor ──────────────────────────────────────────────── */}
        {composing ? (
          <PostEditor
            key="new"
            canPublish={data?.can_publish ?? false}
            categories={data?.categories ?? []}
            onDirtyChange={setDirty}
            onSaved={(slug) => {
              setComposing(false);
              setSelected(slug);
            }}
          />
        ) : activeSlug ? (
          <LoadedPostEditor
            key={activeSlug}
            slug={activeSlug}
            canPublish={data?.can_publish ?? false}
            categories={data?.categories ?? []}
            onDirtyChange={setDirty}
            onDeleted={() => setSelected(null)}
          />
        ) : (
          <Card>
            <p className="m-0 py-12 text-center text-[13px] text-faint">
              Pick an article, or start a new one.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function LoadedPostEditor({
  slug,
  canPublish,
  categories,
  onDirtyChange,
  onDeleted,
}: {
  slug: string;
  canPublish: boolean;
  categories: string[];
  onDirtyChange: (dirty: boolean) => void;
  onDeleted: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useAdminPost(slug);

  if (isError) {
    return (
      <AdminError title="We couldn't open that article" error={error} onRetry={() => refetch()} />
    );
  }

  if (isLoading || !data) {
    return (
      <Card>
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="mt-4 h-[320px] w-full" radius="md" />
      </Card>
    );
  }

  return (
    <PostEditor
      post={data}
      canPublish={canPublish}
      categories={categories}
      onDirtyChange={onDirtyChange}
      onDeleted={onDeleted}
      onSaved={() => undefined}
    />
  );
}

type Draft = {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  category: string;
  sector: '' | 'human' | 'veterinary';
  cover_image_id: number | null;
  cover_url: string | null;
  seo_title: string;
  seo_description: string;
};

function PostEditor({
  post,
  canPublish,
  categories,
  onDirtyChange,
  onSaved,
  onDeleted,
}: {
  post?: BlogPostDetail;
  canPublish: boolean;
  categories: string[];
  onDirtyChange: (dirty: boolean) => void;
  onSaved: (slug: string) => void;
  onDeleted?: () => void;
}) {
  const save = useSavePost();
  const remove = useDeletePost();
  const restore = useRestorePostRevision();
  const upload = useUploadMedia();

  const initial = useMemo<Draft>(
    () => ({
      title: post?.title ?? '',
      slug: post?.slug ?? '',
      excerpt: post?.excerpt ?? '',
      body: post?.body ?? '',
      category: post?.category ?? '',
      sector: (post?.sector ?? '') as Draft['sector'],
      cover_image_id: post?.cover?.id ?? null,
      cover_url: post?.cover?.url ?? null,
      seo_title: post?.seo_title ?? '',
      seo_description: post?.seo_description ?? '',
    }),
    [post],
  );

  const [form, setForm] = useState<Draft>(initial);
  const [note, setNote] = useState('');
  const [showSeo, setShowSeo] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  useUnsavedGuard(dirty);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const validation = save.error instanceof ApiError ? save.error.fieldErrors : undefined;

  const submit = async (status: 'draft' | 'published') => {
    const saved = await save.mutateAsync({
      slug: post?.slug ?? null,
      post: {
        title: form.title.trim(),
        // The slug is only sent when somebody typed one. Left empty, the server
        // derives it and de-duplicates it — "Malaria season" is an article
        // somebody writes every year.
        slug: post ? form.slug.trim() || undefined : undefined,
        excerpt: form.excerpt.trim() || null,
        body: form.body,
        status,
        category: form.category.trim() || null,
        sector: form.sector || null,
        cover_image_id: form.cover_image_id,
        seo_title: form.seo_title.trim() || null,
        seo_description: form.seo_description.trim() || null,
        change_note: note.trim() || null,
      },
    });

    setNote('');
    onDirtyChange(false);
    onSaved(saved.slug);
  };

  const stats = countWords(form.body);

  return (
    <Card className="!p-0">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit(post?.status === 'published' && canPublish ? 'published' : 'draft');
        }}
      >
        <div className="space-y-4 p-4 xl:p-5">
          {/* ── Headline ─────────────────────────────────────────────── */}
          <Input
            label="Headline"
            value={form.title}
            onChange={(event) => set('title', event.target.value)}
            placeholder="How to spot a counterfeit pack"
            required
            maxLength={160}
            error={validation?.title?.[0]}
            data-testid="post-title"
          />

          <div>
            <label
              htmlFor="post-excerpt"
              className="mb-[5px] block text-[11px] font-bold text-muted"
            >
              Standfirst
            </label>
            <textarea
              id="post-excerpt"
              value={form.excerpt}
              onChange={(event) => set('excerpt', event.target.value)}
              rows={2}
              maxLength={300}
              placeholder="One sentence. This is what shows on the card and in a Google result."
              className="block w-full rounded-btn border border-line bg-white p-2.5 text-[13px] leading-relaxed text-ink outline-none focus:border-blue"
            />
            <p className="m-0 mt-1 text-[10.5px] text-faint">
              {form.excerpt.length}/300 · left empty, the card shows nothing but the headline.
            </p>
          </div>

          {/* ── Shelf, category and cover ────────────────────────────── */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="m-0 mb-[5px] text-[11px] font-bold text-muted">Which shelf</p>
              <div className="grid grid-cols-3 gap-1.5">
                {(
                  [
                    ['', 'Both', 'Safety, how ChiCare works'],
                    ['human', 'Pharmacy', 'People'],
                    ['veterinary', 'Veterinary', 'Animals'],
                  ] as const
                ).map(([value, label, hint]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => set('sector', value)}
                    aria-pressed={form.sector === value}
                    data-testid={`post-shelf-${value || 'both'}`}
                    className={cn(
                      'rounded-btn border px-2 py-2 text-left',
                      form.sector === value
                        ? 'border-blue bg-blue-tint'
                        : 'border-line bg-white hover:border-blue/40',
                    )}
                  >
                    <span
                      className={cn(
                        'block text-[12px] font-semibold',
                        form.sector === value ? 'text-blue' : 'text-ink',
                      )}
                    >
                      {label}
                    </span>
                    <span className="mt-0.5 block text-[10px] leading-tight text-faint">
                      {hint}
                    </span>
                  </button>
                ))}
              </div>
              {/* The one sentence that stops the commonest mistake here. */}
              <p className="m-0 mt-1 text-[10.5px] leading-snug text-faint">
                An article about dosing belongs on one shelf. A veterinary piece must never appear
                among human medicines.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor="post-category"
                  className="mb-[5px] block text-[11px] font-bold text-muted"
                >
                  Category
                </label>
                <input
                  id="post-category"
                  list="post-categories"
                  value={form.category}
                  onChange={(event) => set('category', event.target.value)}
                  maxLength={40}
                  placeholder="Safety"
                  className="block min-h-10 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink outline-none focus:border-blue"
                />
                {/* A datalist rather than a dropdown: the existing categories
                    are offered, and a new one is just typed. A join table for
                    five words is machinery nobody needed. */}
                <datalist id="post-categories">
                  {categories.map((one) => (
                    <option key={one} value={one} />
                  ))}
                </datalist>
              </div>

              <CoverPicker
                url={form.cover_url}
                busy={upload.isPending}
                onPick={async (file) => {
                  const asset = await upload.mutateAsync({
                    file,
                    collection: 'blog',
                    alt: form.title || undefined,
                  });

                  set('cover_image_id', asset.id);
                  set('cover_url', asset.url);
                }}
                onClear={() => {
                  set('cover_image_id', null);
                  set('cover_url', null);
                }}
              />
            </div>
          </div>
        </div>

        {/* ── The body ────────────────────────────────────────────────── */}
        <div className="border-t border-line px-4 py-4 xl:px-5">
          <div className="mb-1.5 flex items-baseline justify-between">
            <label htmlFor="post-body" className="text-[11px] font-bold text-muted">
              The article
            </label>
            <span className="text-[10.5px] text-faint">
              {stats.words.toLocaleString()} words · {stats.minutes} min read
            </span>
          </div>

          <MarkdownEditor
            id="post-body"
            value={form.body}
            onChange={(next) => set('body', next)}
            placeholder={
              'Open with the thing a reader came for.\n\n' +
              '## A section heading\n\n' +
              'Then the detail. **Bold** for the part that matters, and a list where a\n' +
              'sentence would carry four separate things:\n\n' +
              '- one\n- two\n\n' +
              'Drag a picture in, or paste one.'
            }
            onUploadImage={async (file) => {
              try {
                const asset = await upload.mutateAsync({ file, collection: 'blog' });

                return asset.url;
              } catch {
                return null;
              }
            }}
          />

          {validation?.body?.[0] ? (
            <p className="m-0 mt-1.5 text-[11.5px] text-danger">{validation.body[0]}</p>
          ) : null}
        </div>

        {/* ── SEO, folded away ────────────────────────────────────────── */}
        <div className="border-t border-line px-4 py-3 xl:px-5">
          <button
            type="button"
            onClick={() => setShowSeo(!showSeo)}
            aria-expanded={showSeo}
            className="flex min-h-9 items-center gap-1.5 text-[12px] font-semibold text-muted hover:text-ink"
          >
            <Icon name={showSeo ? 'up' : 'down'} size={13} />
            Search and sharing
          </button>

          {showSeo ? (
            <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
              <Input
                label="Title in search results"
                value={form.seo_title}
                onChange={(event) => set('seo_title', event.target.value)}
                maxLength={70}
                placeholder={form.title || 'Defaults to the headline'}
                hint={`${form.seo_title.length}/70 — Google truncates past about 60.`}
              />
              <Input
                label="Description in search results"
                value={form.seo_description}
                onChange={(event) => set('seo_description', event.target.value)}
                maxLength={200}
                placeholder={form.excerpt || 'Defaults to the standfirst'}
                hint={`${form.seo_description.length}/200`}
              />
              {post ? (
                <Input
                  label="Address"
                  value={form.slug}
                  onChange={(event) => set('slug', event.target.value)}
                  hint="Changing this breaks every existing link to the article."
                  error={validation?.slug?.[0]}
                  className="font-mono"
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ── Saving ──────────────────────────────────────────────────── */}
        <div className="border-t border-line bg-paper px-4 py-3.5 xl:px-5">
          <Input
            label="What changed (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={300}
            placeholder="Tightened the opening and added the SMS number."
            hint="Kept with the saved version, so a later reader of the history knows why."
          />

          {save.isError && !validation ? (
            <p className="m-0 mt-2.5 rounded-btn bg-danger-tint px-3 py-2 text-[12px] text-danger">
              {save.error instanceof ApiError
                ? save.error.message
                : 'That could not be saved. Try again.'}
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="grey"
              loading={save.isPending}
              disabled={!form.title.trim()}
              onClick={() => void submit('draft')}
              data-testid="save-draft"
              className="w-auto"
            >
              {post?.status === 'published' ? 'Unpublish and save as draft' : 'Save draft'}
            </Button>

            {/* The Publish button is ABSENT without the permission, not
                disabled. A greyed-out button invites somebody to ask why; no
                button, plus the line in the header saying who publishes,
                answers the question before it is asked. */}
            {canPublish ? (
              <Button
                type="button"
                icon="check"
                loading={save.isPending}
                disabled={!form.title.trim()}
                onClick={() => void submit('published')}
                data-testid="publish"
                className="w-auto"
              >
                {post?.status === 'published' ? 'Save and keep live' : 'Publish'}
              </Button>
            ) : null}

            {post ? (
              <>
                <a
                  href={
                    post.sector === 'veterinary' ? `/vet/blog/${post.slug}` : `/blog/${post.slug}`
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                  className="ml-auto flex min-h-10 items-center gap-1.5 rounded-btn px-2 text-[12px] font-semibold text-blue no-underline hover:underline"
                >
                  <Icon name="eye" size={14} />
                  {post.status === 'published' ? 'View it live' : 'Preview the page'}
                </a>

                <Button
                  type="button"
                  variant="bad"
                  icon="trash"
                  loading={remove.isPending}
                  onClick={() => {
                    if (
                      !window.confirm(
                        post.status === 'published'
                          ? `Delete “${post.title}”? It will disappear from the public site immediately.`
                          : `Delete the draft “${post.title}”?`,
                      )
                    ) {
                      return;
                    }

                    remove.mutate(
                      { slug: post.slug },
                      { onSuccess: () => onDeleted?.() },
                    );
                  }}
                  className="w-auto"
                >
                  Delete
                </Button>
              </>
            ) : null}
          </div>

          {remove.isError ? (
            <p className="m-0 mt-2.5 text-[12px] text-danger">
              {remove.error instanceof ApiError
                ? remove.error.message
                : 'That could not be deleted.'}
            </p>
          ) : null}
        </div>
      </form>

      {/* ── The history ─────────────────────────────────────────────── */}
      {post && post.revisions.length > 0 ? (
        <details className="border-t border-line px-4 py-3 xl:px-5">
          <summary className="cursor-pointer text-[12px] font-semibold text-muted">
            {post.revisions.length} saved version{post.revisions.length === 1 ? '' : 's'}
          </summary>

          <ul className="m-0 mt-2.5 list-none space-y-1.5 p-0">
            {post.revisions.map((revision) => (
              <li
                key={revision.version}
                className="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-btn border border-line px-3 py-2"
              >
                <span className="font-mono text-[11px] text-faint">v{revision.version}</span>
                <b className="truncate text-[12px]">{revision.title}</b>
                <span className="text-[11px] text-faint">{revision.editor}</span>
                {revision.change_note ? (
                  <span className="text-[11px] italic text-muted">{revision.change_note}</span>
                ) : null}

                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(`Restore version ${revision.version}?`)) return;

                    restore.mutate({ slug: post.slug, version: revision.version });
                  }}
                  className="ml-auto min-h-9 rounded-btn px-2 text-[11.5px] font-semibold text-blue hover:underline"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}

/** Which shelf, as a mark small enough for a list row. */
function ShelfMark({ sector }: { sector: BlogPostSummary['sector'] }) {
  if (!sector) {
    return <span className="text-[10px] text-faint">both shelves</span>;
  }

  return (
    <span className="flex items-center gap-1 text-[10px] text-muted">
      <Icon name={sector === 'veterinary' ? 'paw' : 'pill'} size={11} />
      {sector === 'veterinary' ? 'vet' : 'pharmacy'}
    </span>
  );
}

function CoverPicker({
  url,
  busy,
  onPick,
  onClear,
}: {
  url: string | null;
  busy: boolean;
  onPick: (file: File) => Promise<void>;
  onClear: () => void;
}) {
  return (
    <div>
      <p className="m-0 mb-[5px] text-[11px] font-bold text-muted">Cover picture</p>

      <div className="flex items-center gap-2.5">
        {url ? (
          <img
            src={url}
            alt=""
            className="h-14 w-24 shrink-0 rounded-btn border border-line object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="grid h-14 w-24 shrink-0 place-items-center rounded-btn border border-dashed border-line bg-paper"
          >
            <Icon name="image" size={16} className="text-faint" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <label className="inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-btn border border-line bg-white px-2.5 text-[12px] font-semibold text-ink hover:border-blue">
            <Icon name={busy ? 'clock' : 'camera'} size={13} />
            {url ? 'Replace' : 'Choose'}
            <input
              type="file"
              accept="image/*"
              data-testid="post-cover"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void onPick(file);
              }}
              className="sr-only"
            />
          </label>

          {url ? (
            <button
              type="button"
              onClick={onClear}
              className="ml-1.5 min-h-9 rounded-btn px-1.5 text-[11.5px] text-muted hover:text-danger"
            >
              Remove
            </button>
          ) : null}

          <p className="m-0 mt-0.5 text-[10px] leading-snug text-faint">
            Landscape, about 1200 × 750. Shown on the card and above the article.
          </p>
        </div>
      </div>
    </div>
  );
}
